// src/components/DataTableView.jsx
import React, { useEffect, useState, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

import { useNavigate, useLocation } from "react-router-dom";
import { FaPlus, FaMinus } from "react-icons/fa";
import {
  FiTrash2,
  FiArchive,
  FiPlay,
  FiEye,
  FiPaperclip,
  FiCopy,
} from "react-icons/fi";
import { BASE_URL, SCRAPING_BASE_URL } from "../utils";
import AppDataGrid from "./AppDataGrid";
import { toast } from "react-toastify";

// AG Grid modules
import { ModuleRegistry } from "ag-grid-community";
import { AllCommunityModule } from "ag-grid-community";
import { MasterDetailModule } from "ag-grid-enterprise";
ModuleRegistry.registerModules([AllCommunityModule, MasterDetailModule]);

export default function DataTableView({ filters, setFilters }) {
  const [data, setData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [perPage, setPerPage] = useState(50);
  const [isLoading, setIsLoading] = useState(false);
  const [showColumnFilterPanel, setShowColumnFilterPanel] = useState(false);
  const [expandedCells, setExpandedCells] = useState({});
  const [expandedCaseId, setExpandedCaseId] = useState(null);
  const [quickFilter, setQuickFilter] = useState("");
  const pageRef = useRef(0);
  const [detailCache, setDetailCache] = useState({});
  const [detailLoading, setDetailLoading] = useState({});
  const detailCacheRef = useRef({});
  const detailLoadingRef = useRef({});

  const gridRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();
  const skipNextTabFetchRef = useRef(true);
  const fetchSeqRef = useRef(0);
  const fetchAbortRef = useRef(null);
  const lastFetchMetaRef = useRef(null);
  const firstGridRenderLoggedRef = useRef(false);

  // Modal state for editing a note
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const editEditorRef = useRef(null);
  const [editSaving, setEditSaving] = useState(false);

  // HTML preview modal for last_note
  const [htmlModalOpen, setHtmlModalOpen] = useState(false);
  const [htmlModalContent, setHtmlModalContent] = useState("");

  // active tab: working / deleted
  const [activeTab, setActiveTab] = useState("working");

  /* ---------------- Utilities ---------------- */
  const stripHtml = (html) => {
    if (!html) return "";
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      return doc.body.textContent || "";
    } catch (e) {
      return String(html).replace(/<[^>]+>/g, "");
    }
  };

  const sanitizeHtmlForView = (html) => {
    if (!html) return "";
    let s = String(html).replace(
      /<(script|style)[\s\S]*?>[\s\S]*?<\/\1>/gi,
      "",
    );
    s = s.replace(/\son\w+="[^"]*"/gi, "");
    s = s.replace(/\son\w+='[^']*'/gi, "");
    return s;
  };

  const isComplexHtml = (html) => {
    if (!html) return false;
    const lowered = String(html).toLowerCase();
    const tagCount = (String(html).match(/<\w+/g) || []).length;
    if (tagCount > 10) return true;
    if (lowered.includes("startfragment")) return true;
    if (
      lowered.includes("<div") ||
      lowered.includes("<table") ||
      lowered.includes("<img")
    )
      return true;
    if (String(html).length > 1000) return true;
    return false;
  };

  const showHtmlContent = (html) => {
    setHtmlModalContent(html || "");
    setHtmlModalOpen(true);
  };

  const firstNWords = (text, n = 20) => {
    if (!text) return "";
    const words = String(text).trim().split(/\s+/).filter(Boolean);
    return words.slice(0, n).join(" ");
  };

  const getRowDetailKey = (row) => {
    if (!row) return "";
    return `${row.CaseID || row.caseId || ""}_${row.petition_court_type || ""}_${row.petition_processing_file_type || ""}`;
  };

  const normalizeDetailResponse = (detail) => {
    if (!detail) return null;
    const grouped = detail.notes || {};
    const flattenedNotes = Array.isArray(grouped)
      ? grouped
      : Object.entries(grouped).flatMap(([status, items]) =>
          (items || []).map((item) => ({
            id: item.noteId || item.id,
            note: item.text || item.note || "",
            status: item.status || status || "Processing",
            label: item.label || "NA",
            remark: item.remark || "NA",
            sub_status: item.sub_status || "NA",
            created_at: item.timestamp || item.created_at || null,
            pdf_url: item.pdf_url || null,
          })),
        );

    flattenedNotes.sort((a, b) => {
      const aTime = new Date(a.created_at || 0).getTime();
      const bTime = new Date(b.created_at || 0).getTime();
      return bTime - aTime;
    });

    return {
      ...detail,
      notes: flattenedNotes,
    };
  };

  const loadDetailForRow = useCallback(async (row) => {
    if (!row?.CaseID) return null;
    const key = getRowDetailKey(row);
    if (!key) return null;
    if (detailCacheRef.current[key] || detailLoadingRef.current[key]) {
      return detailCacheRef.current[key] || null;
    }

    setDetailLoading((prev) => ({ ...prev, [key]: true }));
    try {
      const url = `${BASE_URL}/api/case/${encodeURIComponent(row.CaseID)}/${encodeURIComponent(
        row.petition_court_type || "",
      )}/${encodeURIComponent(row.petition_processing_file_type || "")}`;
      const res = await axios.get(url);
      const normalized = normalizeDetailResponse(res.data);
      setDetailCache((prev) => ({ ...prev, [key]: normalized }));
      return normalized;
    } catch (err) {
      console.error("Failed to load case detail:", err);
      return null;
    } finally {
      setDetailLoading((prev) => ({ ...prev, [key]: false }));
    }
  }, []);

  /* ---------------- Column groups ---------------- */
  const columnGroups = {
    General: [
      "CaseID",
      "pdf_filename",
      "petition_date",
      "status",
      "last_note",
      "note_update",
      "sub_status",
      "remark",
      "label",
      "updated_date",
      "petition_court_type",
      "petition_processing_file_type",
    ],
    Petitioner: [
      "petitioner_name",
      "petitioner_domicile_address",
      "petitioner_city",
      "petitioner_county",
      "petitioner_state",
      "petitioner_zip",
      "petitioner_telephone_number",
      "petitioner_citizenship",
      "petitioner_interest_of_petitioner",
    ],
    Decedent: [
      "decedent_name",
      "decedent_domicile_address",
      "decedent_city",
      "decedent_county",
      "decedent_state",
      "decedent_zip",
      "decedent_township_of",
      "decedent_date_of_death",
      "decedent_place_of_death",
      "decedent_citizenship",
      "decedent_personal_property_value",
      "decedent_real_property_value",
      "decedent_real_property_description",
      "decedent_gross_rent",
      "decedent_special_provision_of_law",
      "decedent_spouse",
      "decedent_children",
      "decedent_any_issue",
      "decedent_mother",
      "decedent_sisters_brothers",
      "decedent_grandmother_grandfather",
      "decedent_aunts_uncles",
      "decedent_first_cousins",
    ],
    Other: ["section3_relief_requested"],
  };

  const allColumns = Object.values(columnGroups).flat();
  const [visibleColumns, setVisibleColumns] = useState(
    new Set(columnGroups.General),
  );

  useEffect(() => {
    const stored = localStorage.getItem("visibleColumns");
    const initial = new Set(stored ? JSON.parse(stored) : columnGroups.General);
    initial.add("remark");
    setVisibleColumns(initial);
  }, []);

  useEffect(() => {
    localStorage.setItem("visibleColumns", JSON.stringify([...visibleColumns]));
  }, [visibleColumns]);

  useEffect(() => {
    detailCacheRef.current = detailCache;
  }, [detailCache]);

  useEffect(() => {
    detailLoadingRef.current = detailLoading;
  }, [detailLoading]);

  /* ---------------- Fetch ---------------- */
  const fetchInitialData = async (endpoint = null) => {
    const requestId = ++fetchSeqRef.current;
    const startedAt = performance.now();
    if (fetchAbortRef.current) {
      try {
        fetchAbortRef.current.abort();
      } catch {}
    }
    const controller = new AbortController();
    fetchAbortRef.current = controller;
    lastFetchMetaRef.current = { requestId, startedAt };
    firstGridRenderLoggedRef.current = false;

    setIsLoading(true);
    console.info("[DataTableView] fetch start", {
      requestId,
      activeTab,
      endpoint: endpoint || (activeTab === "deleted" ? "/api/data/deletedData" : "/api/data"),
    });
    try {
      const url =
        endpoint ||
        (activeTab === "deleted"
          ? `${BASE_URL}/api/data/deletedData`
          : `${BASE_URL}/api/data`);

      const responseStartedAt = performance.now();
      const res = await axios.post(url, filters || {}, { signal: controller.signal });
      const responseReceivedAt = performance.now();
      console.info("[DataTableView] fetch response", {
        requestId,
        status: res?.status,
        responseMs: Math.round(responseReceivedAt - responseStartedAt),
        rows: Array.isArray(res.data) ? res.data.length : 0,
      });

      const mapStartedAt = performance.now();
      const mappedRows = (res.data || []).map((row, index) => {
        const filename = row.pdf_filename?.split("\\").pop() || "";
        const caseIdMatch = filename.match(
          /File_No_(\d{4}-\d{1,4}(?:_[A-Z0-9]+)?)(?=_\d{4}-\d{2}-\d{2})/,
        );
        const caseId = caseIdMatch
          ? caseIdMatch[1]
          : row.caseId || `Unknown-${index}`;

        return {
          ...row,
          CaseID: caseId,
          note_update: row.note_update || row.latest_date || null,
          note_update_text:
            row.note_update_text || row.last_note || row.note || "",
          remark: row.remark ?? "NA",
          note_count: Number(row.note_count ?? row.notes_count ?? 0),
        };
      });
      const mapEndedAt = performance.now();

      if (requestId !== fetchSeqRef.current) {
        console.info("[DataTableView] fetch ignored (stale)", { requestId });
        return;
      }

      setData(mappedRows);
      requestAnimationFrame(() => {
        if (requestId !== fetchSeqRef.current) return;
        const totalMs = Math.round(performance.now() - startedAt);
        console.info("[DataTableView] fetch applied", {
          requestId,
          rows: mappedRows.length,
          mapMs: Math.round(mapEndedAt - mapStartedAt),
          totalMs,
        });
      });
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
        console.info("[DataTableView] fetch canceled", { requestId });
        return;
      }
      console.error("Fetch error:", err);
      setData([]);
    } finally {
      if (requestId === fetchSeqRef.current) {
        setIsLoading(false);
        console.info("[DataTableView] fetch loading cleared", {
          requestId,
          totalMs: Math.round(performance.now() - startedAt),
        });
      }
    }
  };

  useEffect(() => {
    if (location.state?.pageData) {
      setData(location.state.pageData);
      setPerPage(location.state.perPage || 50);
      if (location.state.filters && typeof setFilters === "function") {
        setFilters(location.state.filters || {});
      }
    } else {
      fetchInitialData();
    }
    skipNextTabFetchRef.current = true;
  }, []);

  useEffect(() => {
    if (skipNextTabFetchRef.current) {
      skipNextTabFetchRef.current = false;
      return;
    }
    setSelectedRows([]);
    setExpandedCells({});
    setExpandedCaseId(null);
    fetchInitialData();
  }, [activeTab]);

  useEffect(() => {
    if (isLoading || !data.length || firstGridRenderLoggedRef.current) return;
    const meta = lastFetchMetaRef.current;
    if (!meta?.requestId) return;
    firstGridRenderLoggedRef.current = true;
    console.info("[DataTableView] first usable grid render", {
      requestId: meta.requestId,
      rows: data.length,
      sinceFetchStartMs: Math.round(performance.now() - meta.startedAt),
    });
  }, [data, isLoading]);

  /* ---------------- Actions ---------------- */
  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) return;
    if (!window.confirm(`Delete ${selectedRows.length} records?`)) return;

    try {
      await axios.delete(`${BASE_URL}/api/data/delete`, {
        data: {
          rows: selectedRows.map((r) => ({
            caseId: r.CaseID,
            petition_processing_file_type: r.petition_processing_file_type,
            petition_court_type: r.petition_court_type,
          })),
        },
      });
      await fetchInitialData();
      setSelectedRows([]);
    } catch (err) {
      alert("Failed to delete records.");
    }
  };

  const buildPdfHref = (pdf_url) => {
    if (!pdf_url) return null;
    const s = String(pdf_url);
    if (s.startsWith("http://") || s.startsWith("https://")) return s;

    const base = SCRAPING_BASE_URL || BASE_URL;
    const path = s.startsWith("/") ? s : `/${s}`;
    // NOTE: you previously returned BASE_URL here; keeping your behavior.
    return `${BASE_URL}${path}`;
  };

  async function copyToClipboard(
    text,
    successMsg = "Copied",
    failMsg = "Copy failed",
  ) {
    const payload = text == null ? "" : String(text);

    if (
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function"
    ) {
      try {
        await navigator.clipboard.writeText(payload);
        toast.success(successMsg);
        return true;
      } catch (err) {
        console.warn("clipboard.writeText failed, fallback:", err);
      }
    }

    try {
      const textarea = document.createElement("textarea");
      textarea.value = payload;
      textarea.style.position = "fixed";
      textarea.style.top = "-9999px";
      textarea.style.left = "-9999px";
      textarea.style.width = "1px";
      textarea.style.height = "1px";
      textarea.style.opacity = "0";

      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      textarea.setSelectionRange(0, textarea.value.length);

      const ok = document.execCommand("copy");
      document.body.removeChild(textarea);

      if (ok) {
        toast.success(successMsg);
        return true;
      }
      toast.error(failMsg);
      return false;
    } catch (err) {
      console.error("Fallback copy failed:", err);
      toast.error(failMsg);
      return false;
    }
  }

  /* ---------------- Notes UI ---------------- */
  const NoteItem = ({ note, caseId, onUpdate, onDelete }) => {
    const [isEditingInline, setIsEditingInline] = useState(false);
    const [editedText, setEditedText] = useState(note.note || "");

    useEffect(() => {
      setEditedText(note.note || "");
    }, [note.note]);

    const openEditModal = () => {
      setEditingNote({ ...note, caseId });
      setEditModalOpen(true);
      setTimeout(() => {
        if (editEditorRef.current) {
          editEditorRef.current.innerHTML = note.note || "";
          editEditorRef.current.focus();
        }
      }, 60);
    };

    const pdfHref = buildPdfHref(note.pdf_url || note.pdfUrl || "");

    return (
      <div
        className={`text-sm mb-2 pl-2 border-l-4 ${
          note.status === "Approved" ? "border-green-500" : "border-red-500"
        }`}
      >
        {isEditingInline ? (
          <>
            <textarea
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
              className="text-xs border px-2 py-1 rounded w-[500px]"
            />
            <div className="flex gap-2 mt-1">
              <button
                onClick={() => {
                  if (!editedText.trim()) return alert("Note cannot be empty!");
                  onUpdate(editedText.trim());
                  setIsEditingInline(false);
                }}
                className="text-green-600 text-xs"
              >
                💾 Save
              </button>
              <button
                onClick={() => setIsEditingInline(false)}
                className="text-gray-500 text-xs"
              >
                ❌ Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <div
              className="text-gray-800 whitespace-pre-line"
              dangerouslySetInnerHTML={{ __html: note.note || "" }}
            />
            {pdfHref ? (
              <div className="mt-1 mb-1 flex items-center">
                <a
                  href={pdfHref}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-blue-600 underline flex items-center gap-1"
                >
                  <FiPaperclip size={12} /> <span>View Attached PDF</span>
                </a>
              </div>
            ) : null}
            <div className="text-[11px] text-gray-500 mt-1 flex items-center justify-between">
              <span>
                {note.created_at
                  ? new Date(note.created_at).toLocaleString()
                  : ""}
              </span>
              <div className="flex gap-3 items-center">
                <button
                  onClick={openEditModal}
                  className="text-indigo-600 text-xs"
                >
                  ✏️ Edit
                </button>
                <button onClick={onDelete} className="text-red-500 text-xs">
                  <FiTrash2 size={14} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const handleSaveEditModal = async () => {
    if (!editingNote) return;
    const noteId = editingNote.id || editingNote.noteId;
    if (!noteId) return alert("No note id to save.");

    const html = editEditorRef.current
      ? editEditorRef.current.innerHTML
      : editingNote.note || "";

    if (!html || html.trim() === "") return alert("Note cannot be empty.");

    setEditSaving(true);
    try {
      await axios.put(`${BASE_URL}/api/case/${editingNote.caseId}/edit-note`, {
        noteId,
        caseId: editingNote.caseId,
        newText: html,
      });
      setEditModalOpen(false);
      setEditingNote(null);
      await fetchInitialData();
    } catch (err) {
      console.error("Error saving edited note:", err);
      alert("Failed to save note.");
    } finally {
      setEditSaving(false);
    }
  };

  const DetailCellRenderer = (props) => {
    const row = props.data;
    const detailKey = getRowDetailKey(row);
    const detail = detailCache[detailKey] || null;
    const isLoadingDetail = !!detailLoading[detailKey];
    const notes = detail?.notes || [];
    const detailStatus = detail?.status || row.status || "Processing";
    const detailSubStatus = detail?.sub_status || row.sub_status || "NA";
    const detailRemark = detail?.remark || row.remark || "NA";
    const detailLabel = detail?.label || row.label || "NA";
    const detailFollowUp = detail?.followUp_date || row.followUp_date || row.latest_date || null;
    const detailNoteCount = Number(detail?.note_count ?? row.note_count ?? notes.length ?? 0);

    return (
      <div
        className="p-3 border-t"
        style={{
          whiteSpace: "normal",
          background: "var(--surface)",
          color: "var(--fg)",
          borderColor: "var(--border)",
        }}
      >
        <div className="mb-2 text-xs">
          <strong>Status:</strong> {detailStatus || "Processing"}
        </div>
        <div className="mb-2 text-xs">
          <strong>Sub-Status:</strong> {detailSubStatus || "NA"}
        </div>
        <div className="mb-2 text-xs">
          <strong>Remark:</strong> {detailRemark || "NA"}
        </div>
        <div className="mb-2 text-xs">
          <strong>Label:</strong> {detailLabel || "NA"}
        </div>
        <div className="mb-2 text-xs">
          <strong>Follow Up:</strong>{" "}
          {detailFollowUp ? dayjs(detailFollowUp).format("MMM D, YYYY") : "NA"}
        </div>
        <div className="mb-2 text-xs">
          <strong>Notes Count:</strong> {detailNoteCount}
        </div>

        <div className="text-xs font-semibold mb-1">Notes:</div>

        <div className="mt-1">
          {isLoadingDetail ? (
            <div className="text-xs text-[var(--fg-muted)] py-2">
              Loading full notes...
            </div>
          ) : notes.length > 0 ? (
            notes.map((note) => (
              <NoteItem
                key={note.id}
                note={note}
                caseId={row.CaseID}
                onUpdate={async (newText) => {
                  try {
                    await axios.put(
                      `${BASE_URL}/api/case/${row.CaseID}/edit-note`,
                      {
                        noteId: note.id,
                        caseId: row.CaseID,
                        newText,
                      },
                    );
                    await fetchInitialData();
                    await loadDetailForRow(row);
                  } catch (err) {
                    console.error("Error editing note:", err);
                  }
                }}
                onDelete={async () => {
                  if (window.confirm("Delete this note?")) {
                    try {
                      await axios.delete(
                        `${BASE_URL}/api/case/${row.CaseID}/note/${note.id}`,
                      );
                      await fetchInitialData();
                      await loadDetailForRow(row);
                    } catch (err) {
                      console.error("Error deleting note:", err);
                    }
                  }
                }}
              />
            ))
          ) : (
            <div className="text-xs text-[var(--fg-muted)] py-2">
              No notes available.
            </div>
          )}
        </div>
      </div>
    );
  };

  /* ---------------- Detail toggling ---------------- */
  const toggleDetail = (params) => {
    const api = params.api;
    const node = params.node;
    const row = node?.data;
    const rowId = row?.CaseID;
    const willOpen = !(node && node.expanded);

    api.forEachNode((n) => {
      if (n && n.expanded) n.setExpanded(false);
    });

    if (willOpen) {
      node.setExpanded(true);
      setExpandedCaseId(rowId);
      loadDetailForRow(row);
    } else {
      setExpandedCaseId(null);
    }

    api.refreshCells({ force: true });
    api.resetRowHeights();
  };

  /* ---------------- Width mapping ---------------- */
  const colWidth = (field) => {
    if (field === "CaseID") return 95;
    if (field === "label") return 90;
    if (field === "petition_date") return 140;
    if (field === "status") return 170;
    if (field === "sub_status") return 170;
    if (field === "remark") return 170;
    if (field === "pdf_filename") return 260;
    if (field === "petition_court_type") return 240;
    if (field === "petition_processing_file_type") return 230;
    if (field === "note_update") return 150;
    if (field === "last_note") return 180;
    return 170;
  };

  const statusBadge = {
    approved: "badge-pill badge-green",
    rejected: "badge-pill badge-rose",
    processing: "badge-pill badge-amber",
  };

  const renderTruncated = (params, text) => {
    const row = params.data;
    const field = params.colDef.field;
    const key = `${row.CaseID}-${field}`;
    const expanded = !!expandedCells[key];

    const str = typeof text === "string" ? text : (text ?? "");
    if (str.length <= 50) {
      return <span className="text-xs whitespace-pre-wrap">{str}</span>;
    }

    const shown = expanded ? str : `${str.slice(0, 50)}...`;
    return (
      <span
        className="text-xs"
        style={{ whiteSpace: "normal", wordBreak: "break-word" }}
      >
        {shown}{" "}
        <button
          className="text-[var(--accent)] underline text-[10px]"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setExpandedCells((prev) => ({ ...prev, [key]: !expanded }));
            params.api.refreshCells({
              rowNodes: [params.node],
              columns: [params.column],
              force: true,
            });
            params.api.resetRowHeights();
          }}
        >
          {expanded ? "[View Less]" : "[View More]"}
        </button>
      </span>
    );
  };

  /* ---------------- Column defs ---------------- */
  const columnDefs = [
    ...allColumns
      .filter((c) => visibleColumns.has(c))
      .map((field) => {
        let headerName = field
          .replace(/_/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase());

        if (field === "updated_date") headerName = "Data Updated On";
        if (field === "note_update") headerName = "Notes Updated";

        const width = colWidth(field);

        return {
          field,
          colId: field,
          headerName,
          sortable: true,
          filter: "agTextColumnFilter",
          width,
          minWidth: width,
          maxWidth: width,
          wrapText: true,
          autoHeight: true,

          // IMPORTANT: do NOT force center via flex on all cells (breaks text + can cause layout issues)
          // Instead keep normal flow; only center where you want in renderer.
          cellStyle: { lineHeight: "16px", whiteSpace: "normal" },

          valueGetter: (params) => {
            if (field === "note_update")
              return params.data?.note_update || null;
            return params.data ? params.data[field] : null;
          },

          cellRenderer: (params) => {
            const value = params.value;
            const row = params.data;

            const wrapCenter = (child) => (
              <div className="w-full flex items-center justify-center">
                {child}
              </div>
            );

            if (field === "CaseID") {
              return wrapCenter(
                <a
                  href={`${window.location.origin}/websurrogate/case-details/${row.CaseID}/${row.petition_court_type}/${row.petition_processing_file_type}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[var(--accent)] no-underline text-xs hover:underline"
                >
                  {value}
                </a>,
              );
            }

            if (field === "pdf_filename") {
              const fileOnly = (value?.split("\\").pop() || "").trim();
              const key = `${row.CaseID}-pdf_filename`;
              const expanded = !!expandedCells[key];
              const shown =
                fileOnly.length > 50 && !expanded
                  ? `${fileOnly.slice(0, 50)}...`
                  : fileOnly;

              return wrapCenter(
                <div style={{ textAlign: "center", wordBreak: "break-word" }}>
                  <a
                    href={`${BASE_URL}/static/${fileOnly}`}
                    className="text-[var(--accent)] no-underline text-xs hover:underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {shown}
                  </a>
                  {fileOnly.length > 50 && (
                    <>
                      {" "}
                      <button
                        className="text-[var(--accent)] underline text-[10px]"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setExpandedCells((prev) => ({
                            ...prev,
                            [key]: !expanded,
                          }));
                          params.api.refreshCells({
                            rowNodes: [params.node],
                            columns: [params.column],
                            force: true,
                          });
                          params.api.resetRowHeights();
                        }}
                      >
                        {expanded ? "[View Less]" : "[View More]"}
                      </button>
                    </>
                  )}
                </div>,
              );
            }

            if (field === "status") {
              const status = (value || "Processing").toLowerCase();
              const isExpanded = params.node?.expanded;

              return wrapCenter(
                <div className="flex items-center justify-center gap-2">
                  <span
                    className={
                      statusBadge[status] || "badge-pill badge-neutral"
                    }
                  >
                    {value || "Processing"}
                  </span>

                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleDetail(params);
                    }}
                    className={[
                      "w-6 h-6 rounded-full flex items-center justify-center",
                      "border border-[var(--border)] shadow-sm transition",
                      isExpanded
                        ? "bg-rose-600 text-white hover:bg-rose-700"
                        : "bg-[var(--accent)] text-white hover:bg-[var(--accent-strong)]",
                    ].join(" ")}
                    title={isExpanded ? "Collapse" : "Expand"}
                    type="button"
                  >
                    {isExpanded ? <FaMinus size={10} /> : <FaPlus size={10} />}
                  </button>
                </div>,
              );
            }

            if (field === "sub_status") {
              const sub = row.sub_status?.trim() || "No activity";
              return wrapCenter(renderTruncated(params, sub));
            }

            if (field === "label") {
              const label = (value || "").toUpperCase();
              const cls =
                label === "COLD"
                  ? "ag-label-pill ag-label-pill--cold"
                  : label === "HOT"
                    ? "ag-label-pill ag-label-pill--hot"
                    : label === "WARM"
                      ? "ag-label-pill ag-label-pill--warm"
                      : "ag-label-pill ag-label-pill--na";

              return wrapCenter(<span className={cls}>{label || "NA"}</span>);
            }

            if (field === "petition_date" || field === "updated_date") {
              return wrapCenter(
                <span className="text-xs">
                  {value ? dayjs(value).format("MMM D, YYYY") : ""}
                </span>,
              );
            }

            if (field === "note_update") {
              if (!value) {
                return wrapCenter(
                  <span className="text-[11px] text-[var(--fg-muted)]">
                    No notes
                  </span>,
                );
              }
              const iso = value;
              const moment = dayjs(iso);
              const formatted = moment.format("MMM D, YYYY");
              const rel = moment.fromNow();
              const tooltip = `${iso}${row.note_update_text ? ` — ${row.note_update_text}` : ""}`;

              return wrapCenter(
                <div
                  className="text-[12px]"
                  title={tooltip}
                  style={{ lineHeight: "14px" }}
                >
                  <div className="font-medium text-xs">{formatted}</div>
                  <div className="text-[10px] text-[var(--fg-muted)]">
                    ({rel})
                  </div>
                </div>,
              );
            }

            if (field === "last_note") {
              const raw = value || row.last_note || "";
              const complex = isComplexHtml(raw);
              const plain = stripHtml(raw);
              const key = `${row.CaseID}-last_note`;
              const expandedLocal = !!expandedCells[key];

              const pdfUrlRaw =
                row.note_pdf_url || row.last_note_pdf_url || null;
              const pdfHref = pdfUrlRaw ? buildPdfHref(pdfUrlRaw) : null;

              if (complex) {
                const previewWords = firstNWords(plain, 20);
                return (
                  <div
                    className="w-full flex items-center justify-center"
                    style={{ minHeight: 44 }}
                  >
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          showHtmlContent(raw);
                        }}
                        title="View full HTML"
                        className="p-1 rounded hover:bg-[var(--surface-muted)] flex items-center justify-center"
                        type="button"
                      >
                        <FiEye size={15} className="text-[var(--accent)]" />
                      </button>

                      {pdfHref && (
                        <a
                          href={pdfHref}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          title="Open attached PDF"
                          className="p-1 rounded hover:bg-[var(--surface-muted)] flex items-center justify-center"
                        >
                          <FiPaperclip size={15} className="text-blue-600" />
                        </a>
                      )}

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(
                            plain,
                            "Copied plain text",
                            "Copy failed",
                          );
                        }}
                        title="Copy plain text"
                        className="p-1 rounded hover:bg-[var(--surface-muted)] flex items-center justify-center"
                        type="button"
                      >
                        <FiCopy size={14} className="text-[var(--fg)]" />
                      </button>
                    </div>
                  </div>
                );
              }

              const words = plain.trim().split(/\s+/).filter(Boolean);
              const isLong = words.length > 5;
              const preview = isLong
                ? words.slice(0, 5).join(" ") + "..."
                : words.join(" ");

              return (
                <div
                  className="w-full flex flex-col items-center justify-center text-xs text-center"
                  style={{ minHeight: 44, wordBreak: "break-word" }}
                >
                  <div className="text-[12px] mb-1 whitespace-pre-wrap">
                    {expandedLocal ? plain : preview}
                  </div>

                  <div className="flex items-center justify-center gap-3">
                    {isLong && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedCells((prev) => ({
                            ...prev,
                            [key]: !expandedLocal,
                          }));
                          try {
                            params.api.refreshCells({
                              rowNodes: [params.node],
                              columns: [params.column],
                              force: true,
                            });
                            params.api.resetRowHeights();
                          } catch {}
                        }}
                        className="text-[var(--accent)] text-[12px] underline"
                        type="button"
                      >
                        {expandedLocal ? "View Less" : "View More"}
                      </button>
                    )}

                    {pdfHref && (
                      <a
                        href={pdfHref}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-xs text-[var(--accent)] underline"
                        title="Open attached PDF"
                      >
                        <FiPaperclip size={13} /> View PDF
                      </a>
                    )}

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(
                          plain,
                          "Copied plain text",
                          "Copy failed",
                        );
                      }}
                      title="Copy plain text"
                      className="p-1 rounded hover:bg-[var(--surface-muted)] flex items-center justify-center"
                      type="button"
                    >
                      <FiCopy size={14} className="text-[var(--fg)]" />
                    </button>
                  </div>
                </div>
              );
            }

            return (
              <div
                className="w-full text-xs whitespace-pre-wrap"
                style={{ textAlign: "center" }}
              >
                {value ?? ""}
              </div>
            );
          },
        };
      }),
  ];

  const onSelectionChanged = useCallback((params) => {
    const api = params?.api || gridRef.current?.api;
    if (!api) return;
    const selected = api.getSelectedRows?.() || [];
    setSelectedRows(selected);
  }, []);

  /* ---------------- Tabs (same look, compact) ---------------- */
  const tabWrapperBase =
    "inline-flex items-center gap-2 px-2 py-0.5 rounded-full cursor-pointer transition shadow-sm h-6";
  const workingClasses =
    activeTab === "working"
      ? "bg-green-100 text-green-800 ring-1 ring-green-400 shadow"
      : "bg-[var(--surface)] text-[var(--fg)] border border-[var(--border)] hover:bg-[var(--surface-muted)]";
  const deletedClasses =
    activeTab === "deleted"
      ? "bg-rose-100 text-rose-800 ring-1 ring-rose-400 shadow"
      : "bg-[var(--surface)] text-[var(--fg)] border border-[var(--border)] hover:bg-[var(--surface-muted)]";

  /* ---------------- Render ---------------- */
  return (
    <div className="p-2 md:p-3 w-full min-w-0">
      <div className="relative w-full min-w-0">
        {/* Fullscreen loading overlay (same as CheckCaseStatus) */}
        {isLoading && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
            <div className="surface-card rounded-2xl p-2 flex flex-col items-center">
              <div
                className="h-10 w-10 border-4 border-t-transparent rounded-full animate-spin mb-2"
                style={{
                  borderColor: "var(--accent)",
                  borderTopColor: "transparent",
                }}
              />
              <p className="text-sm font-semibold text-[var(--fg)]">
                Loading, please wait...
              </p>
              <p className="text-xs text-[var(--fg-muted)] mt-1">
                Fetching dashboard data
              </p>
            </div>
          </div>
        )}

        <h2 className="text-md font-semibold mb-2 text-[var(--fg)]">
  Home
</h2>

{/* Controls (glass panel like CheckCaseStatus) */}
<div className="glass-panel rounded-xl p-2.5 border border-[var(--border)] mb-2">
  {/* ✅ LINE 1: Filters + Go/Clear (same row) */}
  <div className="grid grid-cols-12 gap-2 items-center">
    <div className="col-span-12 md:col-span-2">
      <input
        type="date"
        className="field h-8 px-2 text-[11px] rounded w-full"
        value={filters?.startDate || ""}
        onChange={(e) =>
          setFilters({ ...(filters || {}), startDate: e.target.value })
        }
      />
    </div>

    <div className="col-span-12 md:col-span-2">
      <input
        type="date"
        className="field h-8 px-2 text-[11px] rounded w-full"
        value={filters?.endDate || ""}
        onChange={(e) =>
          setFilters({ ...(filters || {}), endDate: e.target.value })
        }
      />
    </div>

    <div className="col-span-12 md:col-span-2">
      <input
        type="text"
        placeholder="Petitioner Name"
        className="field h-8 px-2 text-[11px] rounded w-full"
        value={filters?.petitioner_name || ""}
        onChange={(e) =>
          setFilters({
            ...(filters || {}),
            petitioner_name: e.target.value,
          })
        }
      />
    </div>

    <div className="col-span-12 md:col-span-2">
      <input
        type="text"
        placeholder="Decedent Name"
        className="field h-8 px-2 text-[11px] rounded w-full"
        value={filters?.decedent_name || ""}
        onChange={(e) =>
          setFilters({
            ...(filters || {}),
            decedent_name: e.target.value,
          })
        }
      />
    </div>

    <div className="col-span-12 md:col-span-2">
      <select
        className="field h-8 px-2 text-[11px] rounded w-full"
        value={filters?.court || ""}
        onChange={(e) =>
          setFilters({ ...(filters || {}), court: e.target.value })
        }
      >
        <option value="">Select County Court</option>
        <option value="Queens County Surrogate's Court">
          Queens County Court
        </option>
        <option value="Bronx County Surrogate's Court">
          Bronx County Court
        </option>
        <option value="Kings County Surrogate's Court">
          Kings County Court
        </option>
      </select>
    </div>

    {/* ✅ Petition type + buttons on SAME line */}
    <div className="col-span-12 md:col-span-2 flex items-center gap-2">
      <select
        className="field h-8 px-2 text-[11px] rounded w-full"
        value={filters?.petition_type || ""}
        onChange={(e) =>
          setFilters({
            ...(filters || {}),
            petition_type: e.target.value,
          })
        }
      >
        <option value="">Select Petition Type</option>
        <option value="Probate Petition">Probate Petition</option>
        <option value="ADMINISTRATION PETITION">
          Administration Petition
        </option>
        <option value="Voluntary Administration">
          Voluntary Administration
        </option>
        <option value="Preliminary Letters (Probate or Administration)">
          Preliminary Letters
        </option>
      </select>
    </div>
  </div>

  {/* ✅ LINE 2: Results/tabs left + tools right */}
  <div className="grid grid-cols-12 gap-2 items-center mt-2">
    <div className="col-span-12 md:col-span-6 flex items-center gap-2">
      <h3 className="text-xs font-semibold text-[var(--fg)]">
        Results
      </h3>

      <div
        role="button"
        onClick={() => setActiveTab("working")}
        className={`${tabWrapperBase} ${workingClasses} h-7 px-2 text-[11px]`}
        title="Show active records"
      >
        <FiPlay className="w-3 h-3" />
        <span className="font-medium">Active</span>
      </div>

      <div
        role="button"
        onClick={() => setActiveTab("deleted")}
        className={`${tabWrapperBase} ${deletedClasses} h-7 px-2 text-[11px]`}
        title="Show deleted records"
      >
        <FiArchive className="w-3 h-3" />
        <span className="font-medium">Deleted</span>
      </div>
    </div>

    <div className="col-span-12 md:col-span-6 flex items-center gap-2 justify-end">
      <input
        type="text"
        placeholder="Search all columns..."
        value={quickFilter}
        onChange={(e) => setQuickFilter(e.target.value)}
        className="field h-8 px-2.5 text-[11px] rounded w-full md:w-[300px]"
      />

      <button
        className="h-8 px-2.5 rounded-lg text-[11px] font-semibold bg-[var(--surface)] text-[var(--fg)] border border-[var(--border)] hover:bg-[var(--surface-muted)] shrink-0"
        onClick={() => setShowColumnFilterPanel((prev) => !prev)}
        type="button"
      >
        Filter Columns
      </button>
      {selectedRows.length > 0 && (
        <button
          onClick={handleDeleteSelected}
          className="bg-rose-600 hover:bg-rose-700 text-white px-2.5 h-8 rounded text-[11px] shrink-0"
          type="button"
        >
          Delete Selected ({selectedRows.length})
        </button>
      )}

      <button
        onClick={() => fetchInitialData()}
        className="btn-primary text-[11px] h-8 px-3 shrink-0"
        type="button"
      >
        Go
      </button>

      <button
        onClick={() => {
          setFilters?.({});
          fetchInitialData();
        }}
        className="h-8 px-3 shrink-0 rounded-lg text-[11px] font-semibold bg-[var(--surface)] text-[var(--fg)] border border-[var(--border)] hover:bg-[var(--surface-muted)] shadow-sm"
        type="button"
      >
        Clear
      </button>

      {/* <div className="flex items-center gap-2 shrink-0">
        <span className="text-xs text-[var(--fg-muted)]">Rows per page:</span>
        <select
          value={perPage}
          onChange={(e) => setPerPage(Number(e.target.value))}
          className="field h-9 rounded px-3 text-xs"
        >
          {[10, 20, 50, 100, 200].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div> */}
    </div>
  </div>
</div>


        {/* Column filter panel */}
        {showColumnFilterPanel && (
          <div className="glass-panel rounded-2xl p-3 border border-[var(--border)] mb-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-[var(--fg)]">
                Columns
              </div>

              <button
                onClick={() =>
                  setVisibleColumns(
                    new Set(
                      visibleColumns.size === allColumns.length
                        ? []
                        : allColumns,
                    ),
                  )
                }
                className="text-xs text-[var(--accent)] underline"
                type="button"
              >
                {visibleColumns.size === allColumns.length
                  ? "Deselect All"
                  : "Select All"}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              {Object.entries(columnGroups).map(([group, fields]) => (
                <div
                  key={group}
                  className="border border-[var(--border)] rounded-xl p-2"
                >
                  <h4 className="font-semibold text-xs mb-2 text-[var(--fg)]">
                    {group}
                  </h4>
                  {fields.map((col) => (
                    <label
                      key={col}
                      className="flex items-center gap-2 text-[11px] mb-1"
                    >
                      <input
                        type="checkbox"
                        checked={visibleColumns.has(col)}
                        onChange={() =>
                          setVisibleColumns((prev) =>
                            prev.has(col)
                              ? new Set([...prev].filter((c) => c !== col))
                              : new Set([...prev, col]),
                          )
                        }
                      />
                      <span className="text-[var(--fg)]">
                        {col.replace(/_/g, " ")}
                      </span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Grid wrapper (same pattern as CheckCaseStatus) */}
        <div className="overflow-hidden border border-[var(--border)] rounded-2xl shadow-sm w-full min-w-0">
          <AppDataGrid
            gridRef={gridRef}
            rowData={data}
            columnDefs={columnDefs}
            height={"calc(100vh - 210px)"}
            minWidth={900}
            defaultColDef={{
              resizable: false,
              cellClass: "text-xs",
            }}
            suppressCellFocus={true}
            rowHeight={42}
            headerHeight={36}
            rowSelection={{
              mode: "multiRow",
              checkboxes: true,
              headerCheckbox: true,
              enableClickSelection: false,
              selectionColumnDef: {
                width: 46,
                minWidth: 46,
                maxWidth: 46,
                pinned: false,
                suppressMenu: true,
                sortable: false,
                resizable: false,
                filter: false,
              },
            }}
            masterDetail={true}
            isRowMaster={() => true}
            detailCellRenderer={DetailCellRenderer}
            detailRowAutoHeight={true}
            onSelectionChanged={onSelectionChanged}
            onRowSelected={onSelectionChanged}
            onHeaderCheckboxSelectionChanged={(params) => {
              const api = params.api;
              const page = api.paginationGetCurrentPage?.() || 0;
              const size = api.paginationGetPageSize?.() || perPage;
              const start = page * size;
              const end = start + size;
              const nodes = [];
              api.forEachNode((node) => {
                if (node.rowIndex >= start && node.rowIndex < end) nodes.push(node);
              });
              const allSelected = nodes.length > 0 && nodes.every((n) => n.isSelected());
              api.deselectAll();
              if (!allSelected) nodes.forEach((n) => n.setSelected(true));
              const selected = nodes.filter((n) => n.isSelected()).map((n) => n.data);
              setSelectedRows(selected);
            }}
            onPaginationChanged={(params) => {
              const api = params.api;
              const current = api.paginationGetCurrentPage?.() || 0;
              if (current !== pageRef.current) {
                pageRef.current = current;
                api.deselectAll();
                setSelectedRows([]);
              }
            }}
            pagination={true}
            paginationPageSize={perPage}
            animateRows={true}
            alwaysShowVerticalScroll={true}
            quickFilterText={quickFilter}
            onFirstDataRendered={(params) => {
              if (!firstGridRenderLoggedRef.current && data.length) {
                const meta = lastFetchMetaRef.current;
                if (meta?.requestId) {
                  firstGridRenderLoggedRef.current = true;
                  console.info("[DataTableView] grid first data rendered", {
                    requestId: meta.requestId,
                    rows: data.length,
                    sinceFetchStartMs: Math.round(
                      performance.now() - meta.startedAt,
                    ),
                  });
                }
              }
              params.api.forEachNode((n) => n.setExpanded(false));
            }}
            onFilterChanged={(params) => params.api.resetRowHeights()}
          />
        </div>

        {/* Edit Note Modal */}
        {editModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
            <div className="bg-white rounded shadow-lg w-full max-w-3xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">Edit Note</h3>
                <div className="text-xs text-gray-500">
                  {editingNote?.created_at
                    ? new Date(editingNote.created_at).toLocaleString()
                    : ""}
                </div>
              </div>

              <div
                ref={editEditorRef}
                contentEditable
                suppressContentEditableWarning
                className="border rounded p-2 mb-3 text-sm"
                style={{ minHeight: 180, maxHeight: 400, overflowY: "auto" }}
              />

              {editingNote?.pdf_url || editingNote?.pdfUrl ? (
                <div className="mb-3">
                  <a
                    href={buildPdfHref(
                      editingNote.pdf_url || editingNote.pdfUrl,
                    )}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 underline text-sm inline-flex items-center gap-2"
                  >
                    <FiPaperclip size={14} /> View Attached PDF
                  </a>
                </div>
              ) : null}

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setEditModalOpen(false);
                    setEditingNote(null);
                  }}
                  className="px-3 py-1 border rounded text-sm"
                  type="button"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEditModal}
                  disabled={editSaving}
                  className={`px-4 py-2 rounded text-white ${
                    editSaving
                      ? "bg-gray-400"
                      : "bg-green-600 hover:bg-green-700"
                  }`}
                  type="button"
                >
                  {editSaving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* HTML Viewer Modal */}
        {htmlModalOpen &&
          ReactDOM.createPortal(
            <div className="fixed inset-0 z-50 flex items-center justify-center pt-20 bg-black bg-opacity-40 p-4 overflow-y-auto">
              <div className="bg-white rounded shadow-lg w-full max-w-4xl  p-4 max-h-[80vh] overflow-auto">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold">
                    Last Note — Full HTML
                  </h3>
                  <button
                    onClick={() => setHtmlModalOpen(false)}
                    className="px-2 py-1 border rounded text-sm"
                    type="button"
                  >
                    Close
                  </button>
                </div>

                <div className="prose max-w-full text-sm ">
                  <div
                    className="bg-[var(--surface-muted)] text-[var(--fg-muted)]"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtmlForView(htmlModalContent),
                    }}
                  />
                </div>

                <div className="mt-3 flex justify-end gap-2">
                  <button
                    onClick={() =>
                      copyToClipboard(
                        stripHtml(htmlModalContent),
                        "Copied plain text",
                        "Failed to copy",
                      )
                    }
                    className="px-3 py-1 border rounded text-sm"
                    type="button"
                  >
                    Copy plain text
                  </button>

                  <button
                    onClick={() =>
                      copyToClipboard(
                        htmlModalContent,
                        "Copied raw HTML",
                        "Failed to copy",
                      )
                    }
                    className="px-3 py-1 border rounded text-sm"
                    type="button"
                  >
                    Copy raw HTML
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
}




